import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  CalendarCheck,
  ChevronDown,
  FileText,
  GraduationCap,
  Home,
  LogIn,
  LogOut,
  Menu,
  Search,
  Users,
  Vote,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const NAV_LINKS = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/search', label: 'Search', icon: Search },
  { to: '/gown-request', label: 'Request Gown', icon: CalendarCheck },
  { to: '/booklets', label: 'Booklets', icon: FileText },
];

const ALUMNI_LINKS = [
  { to: '/alumni', label: 'Alumni Committee', icon: Users },
  { to: '/alumni/register', label: 'Register / Login', icon: LogIn },
  { to: '/alumni/nomination', label: 'Nominate', icon: GraduationCap },
  { to: '/vote', label: 'Vote', icon: Vote },
];

const navLinkClass = ({ isActive }) =>
  [
    'relative inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-300 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900',
    isActive ? 'text-gold-300' : 'text-white/85 hover:text-white',
  ].join(' ');

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAlumniOpen, setIsAlumniOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    navigate('/');
  };

  // Close the dropdown on outside click and on Escape.
  useEffect(() => {
    const onPointerDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAlumniOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsAlumniOpen(false);
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Prevent the page behind the mobile drawer from scrolling.
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-brand-700 shadow-header">
      <div className="container-page">
        <nav className="flex h-16 items-center justify-between gap-4" aria-label="Main">
          {/* Brand */}
          <Link
            to="/"
            className="flex flex-shrink-0 items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-300 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900"
          >
            <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-white/10 p-1 ring-1 ring-white/20">
              <img
                src="/images/logo.png"
                alt=""
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement.textContent = 'K';
                  e.currentTarget.parentElement.className +=
                    ' text-white font-extrabold text-lg justify-center items-center flex';
                }}
              />
            </span>
            <span className="hidden sm:block">
              <span className="block text-[0.95rem] font-bold leading-tight tracking-tight text-white">
                Katine TTI
              </span>
              <span className="block text-2xs font-medium uppercase tracking-wider text-gold-300">
                Graduation Management
              </span>
            </span>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden items-center gap-0.5 lg:flex">
            {NAV_LINKS.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} className={navLinkClass}>
                {({ isActive }) => (
                  <>
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                    <span
                      className={`absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-gold-400 transition-opacity ${
                        isActive ? 'opacity-100' : 'opacity-0'
                      }`}
                      aria-hidden="true"
                    />
                  </>
                )}
              </NavLink>
            ))}

            {/* Alumni dropdown — a vertical panel that cannot overflow. */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsAlumniOpen((open) => !open)}
                aria-expanded={isAlumniOpen}
                aria-haspopup="true"
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-white/85 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-300 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900"
              >
                <Users className="h-4 w-4" aria-hidden="true" />
                Alumni
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isAlumniOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>

              {isAlumniOpen && (
                <div
                  className="absolute right-0 mt-2 w-60 origin-top-right overflow-hidden rounded-xl border border-ink-200 bg-white p-1.5 shadow-card-hover animate-scale-in"
                  role="menu"
                >
                  {ALUMNI_LINKS.map(({ to, label, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      role="menuitem"
                      onClick={() => setIsAlumniOpen(false)}
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:bg-brand-50 hover:text-brand-700 focus-visible:bg-brand-50 focus-visible:outline-none"
                    >
                      <Icon className="h-4 w-4 text-brand-600" aria-hidden="true" />
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <span className="mx-2 h-6 w-px bg-white/20" aria-hidden="true" />

            {user ? (
              <>
                <Link
                  to="/admin"
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-white/85 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-300"
                >
                  Dashboard
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-white/85 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-300"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="ml-1 inline-flex items-center gap-1.5 rounded-lg bg-gold-400 px-3.5 py-2 text-sm font-semibold text-brand-950 transition-colors hover:bg-gold-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <LogIn className="h-4 w-4" aria-hidden="true" />
                Staff Login
              </Link>
            )}
          </div>

          {/* Mobile trigger */}
          <button
            type="button"
            onClick={() => setIsMenuOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-300 lg:hidden"
            aria-label="Open menu"
            aria-expanded={isMenuOpen}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {isMenuOpen && (
        <div className="lg:hidden">
          <div
            className="fixed inset-0 z-40 bg-brand-950/60 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            className="fixed inset-y-0 right-0 z-50 flex w-80 max-w-[85vw] flex-col bg-white shadow-2xl animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <div className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
              <span className="text-sm font-bold uppercase tracking-wider text-ink-500">
                Menu
              </span>
              <button
                type="button"
                onClick={() => setIsMenuOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-600 transition-colors hover:bg-ink-100"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-4">
              <p className="px-3 pb-2 text-2xs font-bold uppercase tracking-widest text-ink-400">
                Explore
              </p>
              {NAV_LINKS.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-ink-700 hover:bg-ink-100'
                    }`
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </NavLink>
              ))}

              <p className="px-3 pb-2 pt-5 text-2xs font-bold uppercase tracking-widest text-ink-400">
                Alumni
              </p>
              {ALUMNI_LINKS.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-ink-700 hover:bg-ink-100'
                    }`
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </NavLink>
              ))}

              {user && (
                <>
                  <p className="px-3 pb-2 pt-5 text-2xs font-bold uppercase tracking-widest text-ink-400">
                    Staff
                  </p>
                  <Link
                    to="/admin"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-100"
                  >
                    Dashboard
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" />
                    Logout
                  </button>
                </>
              )}
            </div>

            {!user && (
              <div className="border-t border-ink-200 p-4">
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="btn-secondary w-full"
                >
                  <LogIn className="h-4 w-4" aria-hidden="true" />
                  Staff Login
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
